import clsx from 'clsx';
import type * as React from 'react';

import { ButtonGroup } from '@gitbook/spine-react';

/**
 * Frame used to mark that the block is a reference block.
 * Pointing to another content, example: reusable content or openapi block.
 */
export function ReferenceBlockFrame(
    props: React.ComponentPropsWithoutRef<'div'> & {
        isFocused: boolean;
        isHovered: boolean;
    }
) {
    const { isFocused, isHovered, ...rest } = props;
    return (
        <div
            data-focused={isFocused}
            data-hovered={isHovered}
            {...rest}
            className={clsx(
                'group/frame',
                '-inset-x-1 absolute inset-y-0.5 rounded border border-dashed transition',
                'has-[[aria-expanded=true]]:border-[--sp-color-border-block-reference-frame-active]',
                'has-[:focus]:border-[--sp-color-border-block-reference-frame-active]',
                isFocused
                    ? 'border-[--sp-color-border-block-reference-frame-active]'
                    : isHovered
                      ? 'border-[--sp-color-border-block-reference-frame-default]'
                      : 'border-transparent',
                props.className
            )}
        />
    );
}

/**
 * Actions displayed over the block frame.
 */
export function ReferenceBlockActions(props: { children: React.ReactNode }) {
    return (
        <div
            className={clsx(
                '-top-[calc(0.75rem+1px)] absolute right-1.5 z-[1] flex max-w-[calc(100%-0.75rem)] opacity-0',
                'group-data-[focused=true]/frame:opacity-100 group-data-[hovered=true]/frame:opacity-100',
                'has-[[aria-expanded=true]]:opacity-100',
                'has-[:focus]:opacity-100'
            )}
        >
            {props.children}
        </div>
    );
}

/**
 * Button group meant to be used inside `ReferenceBlockActions`.
 */
export function ReferenceBlockButtonGroup(props: { children: React.ReactNode }) {
    return (
        <ButtonGroup kind="segmented" contentEditable={false} border={false}>
            {props.children}
        </ButtonGroup>
    );
}
